import { Database } from 'libs/supabaseTypes';

export type FeedbackTypeEnum = Database['public']['Enums']['feedback_type'];

export const CHARACTER_SET =
    'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ123456789' as const;

export const CONSUMER_SPONSOR_IDS = ['6454a86e-941f-4fd2-b173-3fe982a62ea7'];

export const YEARS_EXPERIENCE_OPTIONS = [
    '1-5 years',
    '6-10 years',
    '10+ years',
] as const;

export const PURSUIT_YEARS_EXPERIENCE_OPTIONS = [
    '10-12',
    '13-15',
    '16-20',
    '20+',
] as const;

export const FUNDING_STAGE_OPTIONS = [
    'Pre-seed',
    'Seed',
    'Series A',
    'Series B',
    'Series C',
    'Series D',
    'Series E',
    'Series F',
    'Private',
    'Public',
    'Venture series unknown',
    'Nonprofit',
] as const;

export const EMPLOYEE_COUNT_OPTIONS = [
    '1-10 FTE',
    '11-50 FTE',
    '51-200 FTE',
    '200+ FTE',
] as const;

export const COMPANIES_FOUNDED_OPTIONS = [
    '0 (first-time founder)',
    '1',
    '2',
    '3+',
] as const;

export const COACHING_EXPERIENCE_OPTIONS = [
    'Leadership: Expand your Influence at Work',
    'Parenthood: Thriving at Home and Beyond',
    'Career Transitions: Navigate Change, Embrace Your Next Chapter',
] as const;

export const DIAGNOSTIC_RATING = [
    'Strongly Disagree',
    'Disagree',
    'Somewhat Disagree',
    'Neither Agree Nor Disagree',
    'Somewhat Agree',
    'Agree',
    'Strongly Agree',
] as const;

export const MERGED_PRECURSOR_GROUPS = ['PRE20', 'PRE18'];

export const RATING_RESPONSES = [
    'Very Unsatisfied',
    'Unsatisfied',
    'Neutral',
    'Satisfied',
    'Very Satisfied',
] as const;

export const EXIT_RESPONSES = [
    'Strongly Disagree',
    'Disagree',
    'Neutral',
    'Agree',
    'Strongly Agree',
] as const;

export const FEEDBACK_TYPES: Array<FeedbackTypeEnum> = [
    'reinforcing',
    'developmental',
];

export const OPPORTUNITY_QUESTIONS = [
    'How can I help support your growth?',
    'How can I encourage you in this area?',
    'What are some ways you could work on that?',
    'What resources could I provide to help you grow?',
    'What are some ideas you have moving forward?',
    'How can I support you in this area?',
] as const;

export const RESULT_QUESTIONS = [
    'What are your thoughts?',
    'What stood out to you?',
    'What did you notice about the situation?',
    'How did it feel for you?',
] as const;

export const RETRO_DIMENSIONS = {
    'Career Wellbeing': [
        'I am engaged in meaningful work, and I feel confident in my abilities.',
        'I invest time and energy to develop myself.',
        'I take responsibility and create opportunities to evolve my career.',
    ],
    'Emotional Resilience': [
        'I see challenge as an opportunity to expand and engage.',
        'I feel valued and affirmed and take time to value and affirm others.',
        'I have strategies for when I am stressed.',
    ],
    'Environmental Wellbeing': [
        'I have a physical space that enables me to do my best work.',
        'I live and work in a positive environment where I feel safe.',
        'I find time to connect with nature and be outdoors.',
    ],
    'Financial Wellbeing': [
        'I am appropriately compensated for my work.',
        'I feel like I have a healthy relationship with money.',
        'I am satisfied with my current financial situation.',
    ],
    'Intellectual Curiosity': [
        'I give myself permission to take risks, experiment, and be creative.',
        'I take time to engage in self-learning.',
        'I have opportunities to engage in my strengths every day.',
    ],
    'Physical Wellbeing': [
        'I eat healthy and nourishing foods.',
        'I exercise regularly and feel strong.',
        'I get sufficient sleep every night and allow my body to rest when it’s tired.',
    ],
    'Social Wellbeing': [
        'I have a strong support system.',
        'I cultivate trust and connection with others.',
        'I communicate my feelings effectively.',
    ],
    'Spiritual Awareness': [
        'I have a practice that enables me to make meaning in life.',
        'I create time and space to acknowledge and appreciate other beings.',
        'I am living a life that aligns with my personal values.',
    ],
} as const;

export const STATUS = {
    New: 'New',
    InProgress: 'In Progress',
    SelectedMembership: 'Selected Membership',
    Checkout: 'Checkout',
    Paid: 'Paid',
    Completed: 'Completed',
    Confirmed: 'Confirmed',
};

export interface Pillar {
    description: string;
    journey: string;
}

export const PILLARS: Pillar[] = [
    {
        description: 'Reconnecting with who I am beyond work and motherhood',
        journey:
            'I want to rediscover my sense of self and feel more creative, joyful, and fulfilled.',
    },
    {
        description: 'Advancing my career in a way that aligns with my values',
        journey:
            'I want to grow professionally without compromising what matters most to me.',
    },
    {
        description: 'Parenting with confidence while honoring my ambition',
        journey:
            'I want to feel proud of my ambition and present in my family life — not torn between the two.',
    },
    {
        description: 'Leading with authenticity, not perfection',
        journey:
            'I want to show up as my full self at work and at home, without code-switching or hiding parts of who I am.',
    },
    {
        description: 'Designing better systems to protect my time and energy',
        journey:
            'I want to create structure in my life that supports my wellbeing and lets me thrive.',
    },
];

export const PURSUIT_TYPES = [
    'Pursuit Retreat',
    'Pursuit Digital',
    'Pursuit Coach',
] as const;

export const PURSUIT_TIME_SLOTS = [
    '9AM EST / 6AM PST',
    '12PM EST / 9AM PST',
    '3PM EST / 12PM PST',
    '6PM EST / 3PM PST',
] as const;

export const SCHEDULE_TABS = ['Monday', 'Tuesday', 'Wednesday'] as const;

export const ACTIVITY_TYPES = [
    'Content',
    'Relaxation',
    'Food & Drink',
    'Other',
] as const;

export const VALUES = [
    'Abundance',
    'Acceptance',
    'Accountability',
    'Achievement',
    'Advancement',
    'Adventure',
    'Advocacy',
    'Ambition',
    'Appreciation',
    'Attractiveness',
    'Autonomy',
    'Balance',
    'Being the Best',
    'Benevolence',
    'Boldness',
    'Brilliance',
    'Calmness',
    'Caring',
    'Challenge',
    'Charity',
    'Cheerfulness',
    'Cleverness',
    'Community',
    'Commitment',
    'Compassion',
    'Cooperation',
    'Collaboration',
    'Consistency',
    'Contribution',
    'Creativity',
    'Credibility',
    'Curiosity',
    'Daring',
    'Decisiveness',
    'Dedication',
    'Dependability',
    'Diversity',
    'Empathy',
    'Encouragement',
    'Enthusiasm',
    'Ethics',
    'Excellence',
    'Expressiveness',
    'Fairness',
    'Family',
    'Friendships',
    'Flexibility',
    'Freedom',
    'Fun',
    'Generosity',
    'Grace',
    'Growth',
    'Happiness',
    'Health',
    'Honesty',
    'Humility',
    'Humor',
    'Inclusiveness',
    'Independence',
    'Individuality',
    'Innovation',
    'Inspiration',
    'Intelligence',
    'Intuition',
    'Joy',
    'Kindness',
    'Knowledge',
    'Leadership',
    'Learning',
    'Love',
    'Loyalty',
    'Making a Difference',
    'Mindfulness',
    'Motivation',
    'Optimism',
    'Open-Mindedness',
    'Originality',
    'Passion',
    'Peace',
    'Perfection',
    'Performance',
    'Personal Development',
    'Playfulness',
    'Popularity',
    'Power',
    'Preparedness',
    'Proactivity',
    'Professionalism',
    'Punctuality',
    'Quality',
    'Recognition',
    'Relationships',
    'Reliability',
    'Resilience',
    'Resourcefulness',
    'Responsibility',
    'Responsiveness',
    'Risk Taking',
    'Safety',
    'Security',
    'Service',
    'Spirituality',
    'Self-Control',
    'Selflessness',
    'Simplicity',
    'Stability',
    'Success',
    'Teamwork',
    'Thankfulness',
    'Thoughtfulness',
    'Traditionalism',
    'Trustworthiness',
    'Understanding',
    'Uniqueness',
    'Usefulness',
    'Versatility',
    'Vision',
    'Warmth',
    'Wealth',
    'Well-Being',
    'Wisdom',
    'Zeal',
] as const;
